import React, { useEffect, useMemo, useState } from 'react';
import { api, AED_short, AED, getWorkspace } from '../lib/api';
import { motion } from 'framer-motion';
import {
  TrendingUp, TrendingDown, BarChart3, Building2, Sparkles, Loader2, RefreshCcw,
  Flame, MapPin, ArrowUpRight, ArrowDownRight, Zap, Filter, X, Info, Home, ArrowUpDown, ChevronUp, ChevronDown, Search
} from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, CartesianGrid,
} from 'recharts';
import { toast } from 'sonner';
import DealRadarPanel from './DealRadarPanel';
import DealExplainerModal from '../components/DealExplainerModal';
import TopPicksPanel from './TopPicksPanel';
import DataFreshnessBanner from '../components/DataFreshnessBanner';
import YearlyHeatmap from '../components/YearlyHeatmap';

const PIE_COLORS = ['#679286', '#B4906A', '#5A7A9B', '#C87F5B', '#8B7EC8', '#7BAA92'];
const RANGES = [
  { key: 7, label: '7d' }, { key: 30, label: '30d' }, { key: 90, label: '90d' }, { key: 365, label: '1y' },
];

function Delta({ value }) {
  if (value == null || isNaN(value)) return <span className="text-slate-400 text-xs">—</span>;
  const up = value > 0;
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[11px] font-semibold ${up ? 'text-emerald-600' : 'text-red-500'}`}>
      <Icon size={11}/>{Math.abs(value).toFixed(1)}%
    </span>
  );
}

function KPI({ label, value, sub, accent = 'text-slate-800', icon: Icon }) {
  return (
    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
      className="glass p-4 relative overflow-hidden">
      {Icon && <Icon size={18} className={`absolute top-3 right-3 ${accent} opacity-30`}/>}
      <div className="overline">{label}</div>
      <div className={`font-serif text-2xl mt-1 ${accent}`}>{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-1">{sub}</div>}
    </motion.div>
  );
}

// ===== Deal drill-down =====
function DealModal({ id, onClose }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get(`/trends/transactions/${id}`).then(r => setD(r.data)).finally(() => setLoading(false));
  }, [id]);
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm grid place-items-center p-4" onClick={onClose} data-testid="deal-modal">
      <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} onClick={(e) => e.stopPropagation()}
        className="glass-strong w-full max-w-2xl p-8 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-full bg-[#679286]/10 grid place-items-center">
            <BarChart3 size={20} className="text-[#679286]"/>
          </div>
          <div className="flex-1">
            <div className="overline">Closed DLD transaction</div>
            <h2 className="font-serif text-2xl">{d?.building || 'Loading…'}</h2>
            <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5"><MapPin size={11}/>{d?.community}</div>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-slate-100" data-testid="deal-modal-close"><X size={18}/></button>
        </div>
        {loading ? <div className="py-10 text-center text-slate-500"><Loader2 size={16} className="inline animate-spin mr-2"/>Loading…</div> : d ? (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              <KPI label="Sale price" value={AED_short(d.price_aed)} accent="text-[#679286]"/>
              <KPI label="AED/sqft" value={Math.round(d.aed_per_sqft || 0).toLocaleString()}/>
              <KPI label="Size" value={`${d.size_sqft?.toLocaleString()} sqft`}/>
              <KPI label="Date" value={d.transaction_date}/>
            </div>
            <div className="glass p-5 space-y-2 text-sm">
              <Row k="Emirate" v={d.emirate}/>
              <Row k="Community" v={d.community}/>
              <Row k="Building" v={d.building}/>
              <Row k="Property type" v={d.property_type}/>
              <Row k="Bedrooms" v={d.bedrooms === 0 ? 'Studio' : d.bedrooms}/>
              <Row k="Bathrooms" v={d.bathrooms}/>
              <Row k="Built-up" v={`${d.size_sqft?.toLocaleString()} sqft`}/>
              {d.plot_size_sqft && <Row k="Plot" v={`${d.plot_size_sqft?.toLocaleString()} sqft`}/>}
              <Row k="Off-plan" v={d.is_off_plan ? 'Yes' : 'No'}/>
              <Row k="Vacancy at sale" v={d.vacancy_status}/>
              <Row k="Source" v={d.source === 'sample_seed' ? 'Sample data (seed)' : 'Bayut · DLD'}/>
            </div>
          </>
        ) : <div className="py-6 text-center text-slate-400">Not found</div>}
      </motion.div>
    </div>
  );
}
const Row = ({k, v}) => <div className="flex justify-between gap-2"><span className="text-slate-500">{k}</span><span className="font-medium text-right">{v ?? '—'}</span></div>;

// ===== AI Hotspots section =====
function HotspotCard({ h, idx }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-2xl p-4 bg-white/5 hover:bg-white/10 border border-white/10 backdrop-blur-sm transition flex flex-col" data-testid={`hotspot-${idx}`}>
      <div className="flex items-start justify-between mb-2">
        <div className={`font-serif text-3xl ${h.score >= 85 ? 'text-orange-300' : h.score >= 70 ? 'text-amber-300' : 'text-slate-300'}`}>
          {h.score}<span className="text-xs opacity-60">/100</span>
        </div>
        <span className="text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-white/10 text-white">{h.trend}</span>
      </div>
      <div className="text-sm font-semibold text-white mb-1">{h.community}</div>
      <div className="text-xs text-slate-200 mb-2 leading-snug">{h.headline}</div>
      <div className={`text-xs text-slate-300 leading-relaxed ${open ? '' : 'line-clamp-4'}`}>{h.reasoning}</div>
      {h.reasoning && h.reasoning.length > 160 && (
        <button
          onClick={() => setOpen(v => !v)}
          data-testid={`hotspot-toggle-${idx}`}
          className="mt-1 self-start text-[10px] uppercase tracking-widest text-orange-300 hover:text-orange-200"
        >
          {open ? 'Show less' : 'Read more →'}
        </button>
      )}
      {h.suggested_ticket_aed && (
        <div className="mt-3 pt-2 border-t border-white/10 text-[11px] text-orange-200 flex justify-between items-center">
          <span>Ticket: {AED_short(h.suggested_ticket_aed)}</span>
          <span className="capitalize text-slate-300">{h.suggested_property_type}</span>
        </div>
      )}
    </div>
  );
}


// ===== Community leaderboard — sortable table =====
function SortHeader({ label, k, right, sortKey, sortDir, onToggle }) {
  return (
    <button onClick={() => onToggle(k)} data-testid={`sort-${k}`}
      className={`w-full flex items-center gap-1 uppercase tracking-widest text-[10px] font-semibold hover:text-slate-900 ${right ? 'justify-end' : ''}`}>
      {label}
      {sortKey === k ? (sortDir === 'asc' ? <ChevronUp size={11}/> : <ChevronDown size={11}/>) : <ArrowUpDown size={10} className="opacity-40"/>}
    </button>
  );
}

function CommunityLeaderboard({ rows, onOpenCommunity }) {
  const [sortKey, setSortKey] = useState('avg_price_per_sqft');
  const [sortDir, setSortDir] = useState('desc');
  const [q, setQ] = useState('');

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    let xs = s ? rows.filter(r => r.community.toLowerCase().includes(s)) : rows.slice();
    xs.sort((a, b) => {
      const av = a[sortKey] || 0, bv = b[sortKey] || 0;
      if (typeof av === 'string') return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      return sortDir === 'asc' ? av - bv : bv - av;
    });
    return xs;
  }, [rows, q, sortKey, sortDir]);

  const overallAvg = useMemo(() => {
    if (!rows.length) return 0;
    const psfs = rows.map(r => r.avg_price_per_sqft || 0).filter(v => v > 0);
    return psfs.length ? psfs.reduce((s, x) => s + x, 0) / psfs.length : 0;
  }, [rows]);

  const toggleSort = (k) => {
    if (sortKey === k) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(k); setSortDir('desc'); }
  };

  return (
    <div className="glass p-5 mb-6" data-testid="community-leaderboard">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div>
          <div className="overline">Price/sqft by area</div>
          <h3 className="font-serif text-xl mt-1">Community leaderboard · {filtered.length} areas</h3>
          <div className="text-xs text-slate-500 mt-0.5">
            Portfolio-weighted avg: <span className="font-semibold text-slate-700">AED {Math.round(overallAvg).toLocaleString()}/sqft</span>
          </div>
        </div>
        <div className="relative">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search area…"
            data-testid="community-search"
            className="pl-9 pr-3 py-2 rounded-full bg-white/85 border border-white/70 text-xs w-52 outline-none focus:ring-2 focus:ring-[#679286]/30"/>
        </div>
      </div>

      <div className="overflow-x-auto -mx-2">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-slate-500 border-b border-slate-200/60">
              <th className="text-left py-2 px-2 w-8"></th>
              <th className="text-left py-2 px-2"><SortHeader label="Community" k="community" sortKey={sortKey} sortDir={sortDir} onToggle={toggleSort}/></th>
              <th className="text-right py-2 px-2"><SortHeader label="Volume" k="count" right sortKey={sortKey} sortDir={sortDir} onToggle={toggleSort}/></th>
              <th className="text-right py-2 px-2"><SortHeader label="Avg AED/sqft" k="avg_price_per_sqft" right sortKey={sortKey} sortDir={sortDir} onToggle={toggleSort}/></th>
              <th className="text-right py-2 px-2"><SortHeader label="Median AED/sqft" k="median_price_per_sqft" right sortKey={sortKey} sortDir={sortDir} onToggle={toggleSort}/></th>
              <th className="text-right py-2 px-2"><SortHeader label="Total value" k="total_value_aed" right sortKey={sortKey} sortDir={sortDir} onToggle={toggleSort}/></th>
              <th className="text-right py-2 px-2 uppercase tracking-widest text-[10px] font-semibold">vs Avg</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r, i) => {
              const delta = overallAvg ? ((r.avg_price_per_sqft - overallAvg) / overallAvg) * 100 : 0;
              const isHigh = delta > 15, isLow = delta < -15;
              return (
                <tr key={r.community}
                    onClick={() => onOpenCommunity?.(r.community)}
                    data-testid={`community-row-${r.community.replace(/\s+/g,'-').toLowerCase()}`}
                    className="border-b border-slate-100/60 hover:bg-white/60 cursor-pointer transition">
                  <td className="py-2.5 px-2 text-slate-400 font-medium text-xs">{i + 1}</td>
                  <td className="py-2.5 px-2 font-medium text-slate-800">
                    <div className="flex items-center gap-1.5"><MapPin size={11} className="text-slate-400"/>{r.community}</div>
                  </td>
                  <td className="py-2.5 px-2 text-right text-slate-600">{r.count.toLocaleString()}</td>
                  <td className={`py-2.5 px-2 text-right font-semibold ${isHigh ? 'text-orange-600' : isLow ? 'text-blue-600' : 'text-slate-800'}`}>
                    {Math.round(r.avg_price_per_sqft || 0).toLocaleString()}
                  </td>
                  <td className="py-2.5 px-2 text-right text-slate-600">{Math.round(r.median_price_per_sqft || 0).toLocaleString()}</td>
                  <td className="py-2.5 px-2 text-right text-[#679286] font-semibold">{AED_short(r.total_value_aed)}</td>
                  <td className="py-2.5 px-2 text-right">
                    <span className={`inline-flex items-center gap-0.5 text-[11px] font-semibold ${delta > 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                      {delta > 0 ? <ArrowUpRight size={11}/> : <ArrowDownRight size={11}/>}{Math.abs(delta).toFixed(1)}%
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {filtered.length === 0 && (
        <div className="py-10 text-center text-sm text-slate-400">No areas match &ldquo;{q}&rdquo;.</div>
      )}
      <div className="mt-3 text-[11px] text-slate-500 flex items-center gap-1.5">
        <Info size={11}/> Click any row to drill down all transactions in that community.
      </div>
    </div>
  );
}

// ===== Main page =====
export default function Trends() {
  const [tab, setTab] = useState('sold'); // sold | listings
  const [tx_type] = useState('sale');
  const [days, setDays] = useState(30);
  const [community, setCommunity] = useState('');
  const [propertyType, setPropertyType] = useState('');
  const [summary, setSummary] = useState(null);
  const [series, setSeries] = useState([]);
  const [communities, setCommunities] = useState([]);
  const [txs, setTxs] = useState({items: [], total: 0});
  const [txPage, setTxPage] = useState(1);
  const [openDeal, setOpenDeal] = useState(null);
  const [busy, setBusy] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const fromDate = useMemo(() => new Date(Date.now() - days * 86400000).toISOString().slice(0, 10), [days]);

  const load = async () => {
    setBusy(true);
    try {
      const [s, t, c, x] = await Promise.all([
        api.get(`/trends/summary?tx_type=${tx_type}&from_date=${fromDate}${community?`&community=${community}`:''}${propertyType?`&property_type=${propertyType}`:''}`),
        api.get(`/trends/timeseries?days=${days}&tx_type=${tx_type}`),
        api.get(`/trends/communities?tx_type=${tx_type}&from_date=${fromDate}&top=100`),
        api.get(`/trends/transactions?tx_type=${tx_type}&from_date=${fromDate}&page=${txPage}&page_size=25${community?`&community=${community}`:''}${propertyType?`&property_type=${propertyType}`:''}`),
      ]);
      setSummary(s.data); setSeries(t.data); setCommunities(c.data); setTxs(x.data);
    } finally { setBusy(false); }
  };

  useEffect(() => { load(); }, [days, community, propertyType, txPage]);

  const seed = async () => {
    setSeeding(true);
    try {
      const r = await api.post('/trends/seed');
      toast.success(`Seeded ${r.data.transactions.inserted} transactions + ${r.data.listings.inserted} listings`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Seed failed');
    } finally { setSeeding(false); }
  };

  const isEmpty = summary && summary.count === 0;
  const isSampleData = summary && summary.count > 0 && (txs.items[0]?.source === 'sample_seed');
  const typeSplitData = summary?.type_split ? Object.entries(summary.type_split).map(([name, value]) => ({name, value})) : [];
  const isLiveWorkspace = getWorkspace() === 'live';

  return (
    <div className="p-8 max-w-[1500px] mx-auto" data-testid="trends-page">
      <div className="flex items-center gap-4 mb-6">
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#1A2530] to-[#282828] grid place-items-center shadow-lg">
          <BarChart3 className="text-white" size={22}/>
        </div>
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-0.5">Market Intelligence</div>
          <h1 className="font-serif text-3xl">Trends & Analytics</h1>
          <p className="text-sm text-slate-500">Dubai DLD closed transactions · hotspots · community leaderboard · Iris AI insights</p>
        </div>
        {!isLiveWorkspace && (
          <button onClick={seed} disabled={seeding} className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/85 border border-white/70 text-sm hover:bg-white disabled:opacity-60" data-testid="trends-seed">
            {seeding ? <Loader2 size={14} className="animate-spin"/> : <RefreshCcw size={14}/>}
            {isEmpty ? 'Seed sample data' : 'Re-seed sample'}
          </button>
        )}
        {isLiveWorkspace && (
          <BackfillButton onDone={load}/>
        )}
      </div>

      {isLiveWorkspace && !isEmpty && <ArchiveStatsWidget/>}

      {/* Data freshness warning (only renders when a source is stale) */}
      <div className="mb-4">
        <DataFreshnessBanner sources={['dld_transactions','dld_deep_sync','iris_scoring']}/>
      </div>

      {isEmpty && isLiveWorkspace && (
        <div className="glass p-8 text-center mb-6" data-testid="trends-empty-live">
          <BarChart3 size={32} className="mx-auto mb-2 text-slate-300"/>
          <div className="font-serif text-lg mb-1">No transaction history yet</div>
          <div className="text-sm text-slate-500 max-w-lg mx-auto mb-4">
            Real Dubai DLD transactions arrive automatically via the daily 06:00 sync. To pre-fill the last ~1 year now (~30–60s), click below. To browse live inventory, open <a href="/app/market/live" className="underline text-[#679286]">Live Listings</a>.
          </div>
          <BackfillButton onDone={load} variant="empty" />
        </div>
      )}

      {isEmpty && !isLiveWorkspace && (
        <div className="glass p-8 text-center mb-6">
          <BarChart3 size={32} className="mx-auto mb-2 text-slate-300"/>
          <div className="font-serif text-lg mb-1">No market data yet</div>
          <div className="text-sm text-slate-500 mb-4">Load 500 sample Dubai transactions across 30 top communities to explore the trends module.</div>
          <button onClick={seed} disabled={seeding} className="btn-primary text-sm" data-testid="trends-seed-empty">
            {seeding ? 'Loading…' : 'Seed sample data'}
          </button>
        </div>
      )}

      {!isEmpty && (
        <>
          {isSampleData && (
            <div className="rounded-2xl bg-amber-50 border border-amber-200 px-4 py-3 mb-4 flex items-start gap-3" data-testid="sample-data-banner">
              <Info size={16} className="text-amber-600 mt-0.5 shrink-0"/>
              <div className="flex-1 text-sm">
                <div className="font-semibold text-amber-900">Showing sample data</div>
                <div className="text-amber-800 text-xs mt-0.5">
                  Building names + prices are seeded examples for UI testing. Switch to the <span className="font-medium">live</span> workspace and hit <span className="font-medium">Backfill 1y</span> to pull real DLD transactions via bayutapi.dev.
                </div>
              </div>
            </div>
          )}
          <TopPicksPanel/>

          {/* 5-year yearly heatmap — visible above the filters, updates on community change */}
          <div className="mb-6">
            <YearlyHeatmap community={community || null} txType="sale" years={6} />
          </div>

          {/* Filters */}
          <div className="glass p-4 mb-6 flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-1 text-xs text-slate-500 mr-2"><Filter size={12}/> Filters</div>
            <div className="flex gap-1 bg-white/60 rounded-full p-0.5">
              {RANGES.map(r => (
                <button key={r.key} onClick={() => setDays(r.key)} data-testid={`trends-range-${r.label}`}
                  className={`px-3 py-1 rounded-full text-xs font-semibold ${days === r.key ? 'bg-[#1A2530] text-white' : 'text-slate-600'}`}>
                  {r.label}
                </button>
              ))}
            </div>
            <select value={community} onChange={(e) => {setCommunity(e.target.value); setTxPage(1);}} data-testid="trends-community" className="px-3 py-1.5 rounded-full bg-white/85 border border-white/70 text-xs">
              <option value="">All communities</option>
              {communities.map(c => <option key={c.community} value={c.community}>{c.community}</option>)}
            </select>
            <select value={propertyType} onChange={(e) => {setPropertyType(e.target.value); setTxPage(1);}} data-testid="trends-type" className="px-3 py-1.5 rounded-full bg-white/85 border border-white/70 text-xs">
              <option value="">All types</option>
              {['apartment', 'villa', 'townhouse', 'penthouse', 'studio'].map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            {busy && <Loader2 size={14} className="animate-spin text-slate-400"/>}
          </div>

          {/* KPI cards */}
          {summary && (
            <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6" data-testid="trends-kpis">
              <KPI label="Transactions" value={summary.count.toLocaleString()} icon={BarChart3}/>
              <KPI label="Total value" value={AED_short(summary.total_value_aed)} accent="text-[#679286]" icon={TrendingUp}/>
              <KPI label="Avg AED/sqft" value={Math.round(summary.avg_price_per_sqft).toLocaleString()} icon={Building2}/>
              <KPI label="Median AED/sqft" value={Math.round(summary.median_price_per_sqft).toLocaleString()}/>
              <KPI label="Highest deal" value={Math.round(summary.highest_deal?.aed_per_sqft || 0).toLocaleString() + ' /sqft'}
                sub={summary.highest_deal?.community} accent="text-orange-500" icon={TrendingUp}/>
              <KPI label="Lowest deal" value={Math.round(summary.lowest_deal?.aed_per_sqft || 0).toLocaleString() + ' /sqft'}
                sub={summary.lowest_deal?.community} accent="text-blue-600" icon={TrendingDown}/>
            </div>
          )}

          {/* Charts */}
          <div className="grid lg:grid-cols-3 gap-4 mb-6">
            <div className="glass p-5 lg:col-span-2" data-testid="chart-timeseries">
              <div className="overline mb-2">AED/sqft over time</div>
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={series}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb"/>
                  <XAxis dataKey="date" fontSize={10} tickFormatter={d => d?.slice(5)}/>
                  <YAxis fontSize={10} tickFormatter={v => v.toLocaleString()}/>
                  <Tooltip formatter={v => v.toLocaleString()}/>
                  <Line type="monotone" dataKey="avg_price_per_sqft" stroke="#679286" strokeWidth={2} dot={false}/>
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="glass p-5" data-testid="chart-typemix">
              <div className="overline mb-2">Property type mix</div>
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie data={typeSplitData} dataKey="value" nameKey="name" innerRadius={45} outerRadius={80} paddingAngle={2}>
                    {typeSplitData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]}/>)}
                  </Pie>
                  <Legend wrapperStyle={{ fontSize: 10 }}/>
                  <Tooltip/>
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="glass p-5 mb-6" data-testid="chart-communities">
            <div className="overline mb-2">Top communities by volume</div>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={communities.slice(0, 12)}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb"/>
                <XAxis dataKey="community" fontSize={9} interval={0} angle={-25} textAnchor="end" height={80}/>
                <YAxis fontSize={10}/>
                <Tooltip/>
                <Bar dataKey="count" fill="#679286" radius={[8, 8, 0, 0]}/>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Community leaderboard table — sortable */}
          <CommunityLeaderboard rows={communities} onOpenCommunity={(c) => { setCommunity(c); setTxPage(1); }}/>

          {/* Transactions table */}
          <div className="glass p-5" data-testid="deals-table">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="overline">All deals</div>
                <h3 className="font-serif text-xl mt-1">{txs.total.toLocaleString()} transactions · click any row for details</h3>
              </div>
              <div className="text-xs text-slate-500">Page {txPage} of {Math.max(1, Math.ceil(txs.total / 25))}</div>
            </div>
            <div className="overflow-x-auto -mx-2">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200/60">
                    <th className="text-left py-2 px-2">Date</th>
                    <th className="text-left py-2 px-2">Community</th>
                    <th className="text-left py-2 px-2">Building</th>
                    <th className="text-left py-2 px-2">Type</th>
                    <th className="text-right py-2 px-2">Beds</th>
                    <th className="text-right py-2 px-2">Size</th>
                    <th className="text-right py-2 px-2">Price</th>
                    <th className="text-right py-2 px-2">AED/sqft</th>
                  </tr>
                </thead>
                <tbody>
                  {txs.items.map(t => (
                    <tr key={t.id} onClick={() => setOpenDeal(t.id)}
                        data-testid={`deal-row-${t.id}`}
                        className="border-b border-slate-100/60 hover:bg-white/60 cursor-pointer">
                      <td className="py-2 px-2 text-slate-500">{t.transaction_date}</td>
                      <td className="py-2 px-2 font-medium">{t.community}</td>
                      <td className="py-2 px-2 text-slate-600">{t.building || '—'}</td>
                      <td className="py-2 px-2 capitalize text-slate-600">{t.property_type} {t.is_off_plan && <span className="text-[9px] uppercase tracking-widest text-amber-600 ml-1">off-plan</span>}</td>
                      <td className="py-2 px-2 text-right">{t.bedrooms === 0 ? 'Studio' : t.bedrooms}</td>
                      <td className="py-2 px-2 text-right">{t.size_sqft?.toLocaleString()}</td>
                      <td className="py-2 px-2 text-right font-semibold text-[#679286]">{AED_short(t.price_aed)}</td>
                      <td className="py-2 px-2 text-right">{Math.round(t.aed_per_sqft || 0).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between mt-4">
              <button onClick={() => setTxPage(p => Math.max(1, p - 1))} disabled={txPage <= 1}
                className="btn-ghost text-xs disabled:opacity-40" data-testid="deals-prev">← Prev</button>
              <div className="text-xs text-slate-500">Showing {(txPage-1)*25 + 1}–{Math.min(txPage*25, txs.total)} of {txs.total}</div>
              <button onClick={() => setTxPage(p => p + 1)} disabled={txPage * 25 >= txs.total}
                className="btn-ghost text-xs disabled:opacity-40" data-testid="deals-next">Next →</button>
            </div>
          </div>
        </>
      )}

      {openDeal && <DealModal id={openDeal} onClose={() => setOpenDeal(null)}/>}
    </div>
  );
}

function BackfillButton({ onDone, variant }) {
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const run = async () => {
    setConfirming(false);
    setBusy(true);
    try {
      await api.post('/trends/backfill');
      toast.info('Backfill started — grabbing up to 10K DLD transactions (sale + rent). Runs in background, ~3-5 min.');
      const t0 = Date.now();
      const poll = setInterval(async () => {
        try {
          const s = await api.get('/trends/sync-status');
          const job = (s.data.jobs || []).find(j => j.job === 'transactions_backfill');
          if (job && job.status === 'completed') {
            clearInterval(poll);
            setBusy(false);
            toast.success(job.message || `Backfill complete — ${job.total_upserted || 0} rows.`);
            if (onDone) await onDone();
          } else if (job && job.status === 'failed') {
            clearInterval(poll);
            setBusy(false);
            toast.error(job.error || 'Backfill failed.');
          } else if (Date.now() - t0 > 10 * 60 * 1000) {
            clearInterval(poll);
            setBusy(false);
            toast.warning('Still running — reload the page in a couple of minutes.');
          }
        } catch { /* keep polling */ }
      }, 4000);
    } catch (e) {
      setBusy(false);
      const msg = e?.response?.data?.detail || e?.message || 'Backfill failed. Check BAYUT_API_KEY.';
      toast.error(String(msg), { duration: 10000 });
    }
  };

  const promptBtn = variant === 'empty'
    ? (
      <button onClick={() => setConfirming(true)} disabled={busy}
        className="btn-primary text-sm inline-flex items-center gap-2"
        data-testid="trends-backfill-empty">
        {busy ? <Loader2 size={14} className="animate-spin"/> : <RefreshCcw size={14}/>}
        {busy ? 'Backfilling (background)…' : 'Backfill DLD (up to 10K)'}
      </button>
    )
    : (
      <button onClick={() => setConfirming(true)} disabled={busy}
        className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/85 border border-white/70 text-sm hover:bg-white disabled:opacity-60"
        data-testid="trends-backfill">
        {busy ? <Loader2 size={14} className="animate-spin"/> : <RefreshCcw size={14}/>}
        {busy ? 'Backfilling (background)…' : 'Backfill DLD'}
      </button>
    );

  return (
    <>
      {promptBtn}
      {confirming && (
        <div className="fixed inset-0 z-40 grid place-items-center p-4 bg-slate-900/40 backdrop-blur-sm" onClick={() => setConfirming(false)}>
          <div onClick={(e) => e.stopPropagation()} className="glass-strong max-w-md p-8" data-testid="backfill-confirm-modal">
            <h2 className="font-serif text-2xl mb-2">Backfill DLD transactions</h2>
            <p className="text-sm text-slate-600 mb-4 leading-relaxed">
              We&apos;ll pull the latest ~10,000 Dubai DLD transactions (sale + rent) via BayutAPI v3. That&apos;s roughly the last <span className="font-semibold">3-5 weeks</span> of the entire Dubai market.
            </p>
            <p className="text-xs text-slate-500 mb-6">
              Runs in the background (~3-5 min) so you can keep using the app. Uses ~1000 RapidAPI calls.
              <br/><br/>
              <span className="text-emerald-700 font-semibold">Cumulative archive:</span> the twice-daily cron continues capturing new DLD closes (~600/day) into your Mongo forever. In ~3 months you&apos;ll have 50K+ historical rows. In a year, 200K+.
            </p>
            <div className="flex gap-2">
              <button onClick={() => setConfirming(false)} className="flex-1 py-2.5 rounded-full border border-slate-200 text-sm hover:bg-slate-50">Cancel</button>
              <button onClick={run} data-testid="backfill-confirm-btn" className="flex-1 py-2.5 rounded-full bg-slate-900 text-white text-sm hover:bg-slate-800">Start backfill</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function ArchiveStatsWidget() {
  const [s, setS] = useState(null);
  useEffect(() => {
    let mounted = true;
    api.get('/trends/archive-stats')
      .then(r => { if (mounted) setS(r.data); })
      .catch(() => {});
    return () => { mounted = false; };
  }, []);
  if (!s || (s.transactions_total === 0 && s.listings_active === 0)) return null;

  return (
    <div className="glass p-5 mb-6" data-testid="archive-stats">
      <div className="flex items-start gap-3 mb-3">
        <div className="w-9 h-9 rounded-full bg-emerald-500/10 grid place-items-center">
          <BarChart3 size={16} className="text-emerald-600"/>
        </div>
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-0.5">Cumulative Archive · Iris Realty proprietary</div>
          <div className="font-serif text-lg">Your Dubai market intelligence dataset</div>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        <div className="p-3 rounded-2xl bg-white/70 border border-white/70">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">DLD transactions</div>
          <div className="font-serif text-2xl mt-1 text-[#679286]">{(s.transactions_total || 0).toLocaleString()}</div>
          <div className="text-[10px] text-slate-500 mt-0.5">
            {s.transactions_earliest_date ? `since ${s.transactions_earliest_date.slice(0,10)}` : '—'}
          </div>
        </div>
        <div className="p-3 rounded-2xl bg-white/70 border border-white/70">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Added last 30d</div>
          <div className="font-serif text-2xl mt-1 text-emerald-600">+{(s.transactions_synced_last_30d || 0).toLocaleString()}</div>
          <div className="text-[10px] text-slate-500 mt-0.5">via 12-hourly cron</div>
        </div>
        <div className="p-3 rounded-2xl bg-white/70 border border-white/70">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Active listings</div>
          <div className="font-serif text-2xl mt-1">{(s.listings_active || 0).toLocaleString()}</div>
          <div className="text-[10px] text-slate-500 mt-0.5">refreshed every 6h</div>
        </div>
        <div className="p-3 rounded-2xl bg-white/70 border border-white/70">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Off-market archive</div>
          <div className="font-serif text-2xl mt-1 text-slate-500">{(s.listings_off_market || 0).toLocaleString()}</div>
          <div className="text-[10px] text-slate-500 mt-0.5">sold/withdrawn</div>
        </div>
      </div>
    </div>
  );
}
